/*
 * Users, data access, segregation of duties, temporary access, the security
 * policy and two-step sign-in (P0.1, P0.4, P0.6–P0.10).
 *
 * Shapes follow the API (UserController, DataScopeController, SodRuleController,
 * TemporaryAccessController, SecurityPolicyController, MfaController). The
 * sample people, matrix, rules and grants come from mock/admin.js, translated
 * from the prototype's role labels into role names; writes change them in
 * memory until the page is reloaded.
 */
import { users as sampleUsers, accessMatrix, sodRules, makerCheckerList, temporaryAccess, securityPolicy } from "@/mock/admin";
import { staff, teams, departments } from "@/mock/masters";
import { roles } from "./access";
import { ok, created, fail, notFound, invalid, paginate, matches, nextId, daysAgo, hoursAgo, now } from "./util";

const conflict = (message, details = null) => fail(message, { code: "RECORD_CONFLICT", ...(details ? { details } : {}) }, 409);
const today = () => new Date().toISOString().slice(0, 10);

// The signed-in sample person (stores/session.js): Jane Wanjiku, an administrator.
const ME = 1;

/* ------------------------------------------------------------------ */
/* Roles and modules                                                     */
/* ------------------------------------------------------------------ */

const roleByLabel = (label) => roles.find((r) => r.label === label);
const roleByName = (name) => roles.find((r) => r.name === name);
const roleName = (label) => roleByLabel(label)?.name ?? label;
const roleLabel = (name) => roleByName(name)?.label ?? name;
const crmRoles = roles.filter((r) => r.is_active !== false);

export const SCOPES = [
  { key: "none", label: "No access", help: "Module hidden" },
  { key: "own", label: "Own", help: "Records the user owns or is assigned to" },
  { key: "team", label: "Team", help: "Records owned by anyone in the user's team, and by the people who report to them" },
  { key: "department", label: "Department", help: "Records owned by anyone in the user's department" },
  { key: "all", label: "All company", help: "Every record in this company" },
];
const RANK = SCOPES.map((s) => s.key);
const scopeLabel = (key) => SCOPES.find((s) => s.key === key)?.label ?? key;
const widest = (list) => list.reduce((a, b) => (RANK.indexOf(b) > RANK.indexOf(a) ? b : a), "none");

export const MODULES = [
  { key: "prospects", label: "Prospects" },
  { key: "opportunities", label: "Leads & Opportunities" },
  { key: "quotations", label: "Quotations" },
  { key: "customers", label: "Customers" },
  { key: "onboarding", label: "Onboarding" },
  { key: "tickets", label: "Support tickets" },
  { key: "renewals", label: "Renewals" },
  { key: "retention", label: "Churn & Exit" },
  { key: "reports", label: "Reports" },
];
const moduleLabel = (key) => MODULES.find((m) => m.key === key)?.label ?? key;

// role name → { module key → scope }. The administrator always sees everything.
const matrix = Object.fromEntries(
  crmRoles.map((r) => {
    const row = accessMatrix[r.label] ?? [];
    return [r.name, Object.fromEntries(MODULES.map((m, i) => [m.key, r.name === "admin" ? "all" : row[i] ?? "own"]))];
  }),
);

/* ------------------------------------------------------------------ */
/* People                                                                */
/* ------------------------------------------------------------------ */

const STATUS = { invited: "pending" };
const TYPE_LABELS = { exit: "Exit", transfer: "Transfer", suspension: "Suspension", access_expiry: "Access expiry" };

const lockedUntil = (u) => (u.failed_attempts >= 5 ? hoursAgo(-0.4) : null);

const people = sampleUsers.map((u) => ({
  id: u.id,
  name: u.name,
  email: u.email,
  phone: u.phone,
  staff_id: u.staff_id,
  roles: u.roles.map(roleName),
  status: STATUS[u.status] ?? u.status,
  mfa_enabled: !!u.mfa,
  last_login_at: u.last_login ? new Date(u.last_login).toISOString() : null,
  failed_login_attempts: u.failed_attempts ?? 0,
  locked_until: lockedUntil(u),
  note: u.note ?? null,
  created_at: daysAgo(300 - u.id * 7),
  activated: u.status !== "invited",
}));

const personById = (id) => people.find((p) => String(p.id) === String(id));
const staffById = (id) => staff.find((s) => String(s.id) === String(id));

// The inactive-user register: one row per deactivation, newest first.
const deactivations = sampleUsers
  .filter((u) => u.deactivation)
  .map((u, i) => ({
    id: i + 1,
    user_id: u.id,
    type: u.deactivation.type,
    effective_date: u.deactivation.on,
    note: u.deactivation.note,
    deactivated_by: people.find((p) => p.name === u.deactivation.by) ?? { id: null, name: u.deactivation.by },
    deactivated_at: `${u.deactivation.on}T09:00:00.000Z`,
    reassigned_to: u.deactivation.reassigned_to ?? null,
    reactivation: null,
    reactivated_at: null,
  }));

const openDeactivation = (userId) => deactivations.find((d) => d.user_id === userId && !d.reactivated_at);

const staffSummary = (s) => {
  if (!s) return null;
  const team = teams.find((t) => t.id === s.team_id);
  const dept = departments.find((d) => d.id === team?.department_id);
  const sup = staff.find((x) => x.name === s.supervisor);
  return {
    id: s.id, staff_no: s.staff_no, name: s.name, title: s.role, status: s.status,
    department: dept ? { id: dept.id, name: dept.name } : null,
    team: team ? { id: team.id, name: team.name } : null,
    supervisor: sup ? { id: sup.id, name: sup.name } : null,
  };
};

const mfaRequired = (p) =>
  policy.mfa.mode === "required_all" || (policy.mfa.mode === "required_roles" && p.roles.some((r) => policy.mfa.roles.includes(r)));

const userScope = (p) => widest(MODULES.map((m) => widest(p.roles.map((r) => matrix[r]?.[m.key] ?? "none"))));

const presentDeactivation = (d) =>
  d && {
    id: d.id,
    type: d.type,
    type_label: TYPE_LABELS[d.type] ?? d.type,
    effective_date: d.effective_date,
    note: d.note,
    by: d.deactivated_by?.name ?? null,
    reassigned_to: d.reassigned_to ? { id: d.reassigned_to, name: staffById(d.reassigned_to)?.name ?? "—" } : null,
  };

const presentUser = (p) => {
  const d = openDeactivation(p.id);
  return {
    id: p.id,
    name: p.name,
    email: p.email,
    phone: p.phone,
    job_title: staffById(p.staff_id)?.role ?? null,
    status: p.status,
    activated: p.activated,
    roles: p.roles.map((n) => ({ id: roleByName(n)?.id ?? n, name: n, label: roleLabel(n) })),
    staff: staffSummary(staffById(p.staff_id)),
    created_at: p.created_at,
    is_locked: !!p.locked_until && new Date(p.locked_until) > new Date(),
    locked_until: p.locked_until,
    failed_login_attempts: p.failed_login_attempts,
    mfa_enabled: p.mfa_enabled,
    mfa_required: mfaRequired(p),
    last_login_at: p.last_login_at,
    data_scope: userScope(p),
    note: p.note,
    deactivation: presentDeactivation(d),
    reactivation_pending: d?.reactivation?.status === "pending" ? { reference: d.reactivation.reference } : null,
  };
};

// Records each person still owns, as the ownership registry would count them.
const ownershipOf = (p) => {
  const s = staffById(p.staff_id);
  const n = (p.id * 7) % 13;
  return {
    user: { id: p.id, name: p.name },
    staff: s ? { id: s.id, name: s.name } : null,
    items: s
      ? [
          { key: "teams-led", label: "Teams they lead", count: teams.filter((t) => t.lead === s.name).length, reassignable: false, note: "Change the team lead under Settings → Teams." },
          { key: "departments-headed", label: "Departments they head", count: departments.filter((d) => d.head === s.name).length, reassignable: false, note: "Change the head under Settings → Departments." },
          { key: "supervisees", label: "Staff who report to them", count: staff.filter((x) => x.supervisor === s.name && x.status === "active").length, reassignable: false, note: "Change each person's supervisor under Settings → Staff." },
          { key: "open-records", label: "Open prospects, leads and tasks", count: n, reassignable: true, note: null },
        ].filter((i) => i.count > 0 || i.reassignable)
      : [],
    get total() {
      return this.items.reduce((sum, i) => sum + i.count, 0);
    },
  };
};

/* ------------------------------------------------------------------ */
/* Segregation of duties                                                 */
/* ------------------------------------------------------------------ */

const PROCESSES = [
  { key: "role.permission_change", label: "Role permission change", in_engine: true },
  { key: "role.assignment_change", label: "Role assignment change", in_engine: true },
  { key: "access.temporary", label: "Temporary access", in_engine: true },
  { key: "user.reactivation", label: "User reactivation", in_engine: true },
  { key: "price.change", label: "Package price change", in_engine: false },
  { key: "price_list.approval", label: "Price list", in_engine: false },
  { key: "quotation.discount", label: "Quotation discount", in_engine: false },
  { key: "quotation.approval", label: "Quotation sign-off", in_engine: false },
  { key: "customer.exit", label: "Customer exit", in_engine: false },
  { key: "contract.approval", label: "Contract approval", in_engine: false },
  { key: "renewal.pricing", label: "Renewal pricing", in_engine: false },
  { key: "records.bulk_reassign", label: "Bulk reassignment", in_engine: false },
];

const TYPES = [
  { key: "no_self_approval", label: "No self-approval" },
  { key: "no_self_access", label: "No granting access to yourself" },
  { key: "maker_checker", label: "Maker-checker" },
  { key: "admin_separation", label: "Administrator kept out of commercial approval" },
  { key: "role_conflict", label: "Conflicting roles" },
];

const RULE_SHAPE = {
  no_self_approval: { type: "no_self_approval" },
  own_access: { type: "no_self_access", processes: ["role.assignment_change", "access.temporary", "user.reactivation"] },
  maker_checker_discount: { type: "maker_checker", processes: ["quotation.discount"] },
  maker_checker_exit: { type: "maker_checker", processes: ["customer.exit"] },
  maker_checker_pricing: { type: "maker_checker", processes: ["price.change", "price_list.approval"] },
  maker_checker_reassign: { type: "maker_checker", processes: ["records.bulk_reassign"] },
  admin_separate: { type: "admin_separation", roles: ["admin"], processes: ["quotation.discount", "quotation.approval", "customer.exit", "price.change", "price_list.approval", "contract.approval", "renewal.pricing"] },
};

const rules = [
  ...sodRules
    .filter((r) => RULE_SHAPE[r.key])
    .map((r) => ({ key: r.key, label: r.rule, description: r.explain, is_active: r.on, is_locked: r.locked, ...RULE_SHAPE[r.key] })),
  { key: "conflict_finance_sales", type: "role_conflict", label: "Finance and Sales on one person", description: "Somebody who sells must not also sign off the commercial terms.", roles: ["finance", "sales"], action: "block", is_active: true, is_locked: false },
  { key: "conflict_admin_audit", type: "role_conflict", label: "Administrator and Audit & Risk on one person", description: "The person who verifies access changes should not also approve them.", roles: ["admin", "audit-risk"], action: "flag", is_active: true, is_locked: false },
].map((r, i) => ({ id: i + 1, processes: [], roles: [], action: null, allowed_user_ids: [], ...r }));

const makerChecker = makerCheckerList.map((m, i) => ({
  process: PROCESSES[[6, 7, 5, 8, 11, 1][i] ?? 0].key,
  label: m.action,
  workflow: null,
  checkers: [m.checker, m.fallback].filter((x) => x && x !== "—"),
  enforced: true,
}));

/* ------------------------------------------------------------------ */
/* Temporary access                                                      */
/* ------------------------------------------------------------------ */

const STATUS_LABELS = { pending: "Pending approval", scheduled: "Scheduled", active: "Active", expired: "Expired", revoked: "Revoked", rejected: "Rejected", cancelled: "Cancelled" };

const PERMISSION_CHOICES = [
  { name: "reports.export", label: "Export reports", module: "Reporting" },
  { name: "prospects.export", label: "Export prospects", module: "Prospects" },
  { name: "quotations.view", label: "View quotations", module: "Quotations" },
  { name: "field.package.price.view", label: "See package prices", module: "Users" },
  { name: "approvals.act", label: "Approve or reject requests", module: "System Administration" },
  { name: "exports.create", label: "Export data", module: "Reporting" },
];

const fromSample = (t) => {
  const [kind, what] = t.grant.split(": ");
  const [modText, scopeText] = String(t.scope).split(" · ");
  const module = MODULES.find((m) => m.label.startsWith(modText) || modText.startsWith(m.label))?.key ?? "customers";
  const scope = SCOPES.find((s) => s.label === scopeText)?.key ?? "team";
  const base = { id: t.id, user_id: t.user_id, starts_at: t.start, ends_at: t.expiry, reason: t.reason, status: t.status, revoke_reason: t.revoked_note ?? null };
  const requester = people.find((p) => p.name === t.requested_by);
  const approver = t.approver;
  const who = { requested_by: requester ? { id: requester.id, name: requester.name } : { id: null, name: t.requested_by }, approver_name: approver };
  if (kind === "Role") return { ...base, ...who, type: "role", role: roleByLabel(what.replace(/ \(.*\)$/, "")) ?? roleByName("sales-manager"), permissions: [], module: null, scope: null };
  if (kind === "Permission") return { ...base, ...who, type: "permission", role: null, permissions: [what.includes("export") ? `${module === "reports" ? "reports" : "prospects"}.export` : "field.package.price.view"], module: null, scope: null };
  return { ...base, ...who, type: "scope", role: null, permissions: [], module, scope };
};

const grants = temporaryAccess.map(fromSample).map((g, i) => ({ ...g, reference: `TAG-2026-${String(i + 1).padStart(4, "0")}`, created_at: daysAgo(10 - i) }));

const grantLabel = (g) =>
  g.type === "role" ? `Role: ${g.role?.label}` : g.type === "permission" ? `Permission: ${g.permissions.map((p) => PERMISSION_CHOICES.find((c) => c.name === p)?.label ?? p).join(", ")}` : `Wider scope: ${moduleLabel(g.module)}`;

const presentGrant = (g) => {
  const u = personById(g.user_id);
  const pending = g.status === "pending";
  const self = g.user_id === ME || g.requested_by?.id === ME;
  return {
    id: g.id,
    reference: g.reference,
    user: u ? { id: u.id, name: u.name, email: u.email } : { id: g.user_id, name: "—", email: null },
    type: g.type,
    role: g.role ? { id: g.role.id, name: g.role.name, label: g.role.label } : null,
    permissions: g.permissions,
    module: g.module,
    module_label: g.module ? moduleLabel(g.module) : null,
    scope: g.scope,
    grant_label: grantLabel(g),
    scope_label: g.type === "scope" ? `${moduleLabel(g.module)} · ${scopeLabel(g.scope)}` : g.type === "role" ? "Everything the role carries" : "As the permission allows",
    starts_at: g.starts_at,
    ends_at: g.ends_at,
    reason: g.reason,
    status: g.status,
    status_label: STATUS_LABELS[g.status] ?? g.status,
    requested_by: g.requested_by,
    approver: g.approver_name ? { name: g.approver_name } : null,
    approval: pending
      ? { id: g.id, reference: `APR-2026-${String(100 + g.id).padStart(4, "0")}`, status: "pending", current_step: "Approval — Management", can_act: !self }
      : null,
    activated_at: ["active", "expired", "revoked"].includes(g.status) && g.approver_name ? `${g.starts_at}T00:00:00.000Z` : null,
    expired_at: g.status === "expired" ? `${g.ends_at}T23:59:00.000Z` : null,
    revoked_at: g.status === "revoked" ? g.revoked_at ?? `${g.ends_at}T12:00:00.000Z` : null,
    revoked_by: g.status === "revoked" ? { name: g.revoked_by_name ?? g.approver_name ?? "Mercy Wambui" } : null,
    revoke_reason: g.revoke_reason,
    is_self: self,
    created_at: g.created_at,
  };
};

const findGrant = (id) => grants.find((g) => String(g.id) === String(id));

/* ------------------------------------------------------------------ */
/* Security policy                                                       */
/* ------------------------------------------------------------------ */

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAYS = DAY_KEYS.map((k) => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1) }));

const policy = {
  password: {
    min_length: securityPolicy.password.min_length,
    require_upper: securityPolicy.password.upper,
    require_lower: securityPolicy.password.lower,
    require_number: securityPolicy.password.number,
    require_symbol: securityPolicy.password.symbol,
    disallow_name: securityPolicy.password.no_name,
    expiry_days: securityPolicy.password.expiry_days,
    history: securityPolicy.password.history,
  },
  lockout: { attempts: securityPolicy.lockout.attempts, minutes: securityPolicy.lockout.minutes, notify_admins: securityPolicy.lockout.notify_admin },
  session: { ...securityPolicy.session },
  mfa: { mode: securityPolicy.mfa.mode, roles: securityPolicy.mfa.roles.map(roleName) },
  ip: { enabled: securityPolicy.ip.enabled, ranges: [...securityPolicy.ip.ranges] },
  hours: {
    enabled: securityPolicy.hours.enabled,
    from: securityPolicy.hours.from,
    to: securityPolicy.hours.to,
    days: securityPolicy.hours.days.map((d) => d.toLowerCase()),
    exempt_roles: securityPolicy.hours.exempt_roles.map(roleName),
  },
  temporary_access: { max_days: 14 },
  updated_at: daysAgo(12),
  updated_by: "Mercy Wambui",
};

const MFA_MODES = [
  { key: "off", label: "Off", help: "No second step. Not recommended: commercial values and customer contacts are sensitive." },
  { key: "optional", label: "Optional", help: "People may turn on two-step sign-in for themselves; nobody is made to." },
  { key: "required_roles", label: "Required for roles", help: "People holding any of the ticked roles must enrol before they can work." },
  { key: "required_all", label: "Required for everyone", help: "Everybody enrols at their next sign-in." },
];

/* ------------------------------------------------------------------ */
/* Your own two-step sign-in                                             */
/* ------------------------------------------------------------------ */

const mine = { enabled: false, enabled_at: null, pending: false, recovery_codes_left: 0 };
const SECRET = "JBSWY3DPEHPK3PXP";

// A stand-in QR picture: the prototype has no authenticator to scan it with.
const fakeQr = () => {
  const cells = [];
  let seed = 7;
  for (let y = 0; y < 21; y++)
    for (let x = 0; x < 21; x++) {
      seed = (seed * 9301 + 49297) % 233280;
      const finder = (x < 7 && y < 7) || (x > 13 && y < 7) || (x < 7 && y > 13);
      const on = finder ? (x % 6 === 0 || y % 6 === 0 || (x % 7 > 1 && x % 7 < 5 && y % 7 > 1 && y % 7 < 5)) : seed / 233280 > 0.5;
      if (on) cells.push(`<rect x="${x}" y="${y}" width="1" height="1"/>`);
    }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 23 23" width="184" height="184"><rect x="-1" y="-1" width="23" height="23" fill="#fff"/><g fill="#000">${cells.join("")}</g></svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const enrolment = () => ({
  secret: SECRET,
  otpauth_url: `otpauth://totp/DeeCRM360:demo@deecrm360.test?secret=${SECRET}&issuer=DeeCRM360`,
  qr_svg: fakeQr(),
});

const recoveryCodes = () => Array.from({ length: 8 }, (_, i) => `${(4821 * (i + 3)).toString(36).padStart(5, "x")}-${(9137 * (i + 5)).toString(36).padStart(5, "q")}`);
const codeOk = (code) => /^\d{6}$/.test(String(code ?? "").trim()) || /^[a-z0-9]{5}-[a-z0-9]{5}$/i.test(String(code ?? "").trim());

const tenantSession = () => ({
  user: { id: ME, name: "Jane Wanjiku", email: "demo@deecrm360.test", phone: "+254 722 410 118", job_title: "Sales Manager", status: "active" },
  roles: ["admin"],
  permissions: [],
  has_all_permissions: true,
  mfa: { enabled: mine.enabled, required: false },
  data_scopes: Object.fromEntries(MODULES.map((m) => [m.key, "all"])),
  temporary_access: [],
});

/* ------------------------------------------------------------------ */
/* Routes                                                                */
/* ------------------------------------------------------------------ */

const isLocked = (p) => !!p.locked_until && new Date(p.locked_until) > new Date();

export default [
  /* ---- Users (P0.1, P0.8) ---- */
  ["get", "tenant/users-summary", () => {
    const current = people.filter((p) => ["active", "pending"].includes(p.status));
    return ok({
      total: people.length,
      active: people.filter((p) => p.status === "active").length,
      pending: people.filter((p) => p.status === "pending").length,
      suspended: people.filter((p) => p.status === "suspended").length,
      deactivated: people.filter((p) => p.status === "deactivated").length,
      locked: people.filter(isLocked).length,
      mfa_enabled: current.filter((p) => p.mfa_enabled).length,
      mfa_required_missing: current.filter((p) => mfaRequired(p) && !p.mfa_enabled).length,
    });
  }],

  ["get", "tenant/users-inactive", ({ query }) => {
    const rows = deactivations
      .filter((d) => !query.type || d.type === query.type)
      .filter((d) => matches(query.search, personById(d.user_id)?.name, personById(d.user_id)?.email))
      .sort((a, b) => String(b.deactivated_at).localeCompare(String(a.deactivated_at)))
      .map((d) => {
        const p = personById(d.user_id);
        return {
          id: d.id,
          user: { id: p.id, name: p.name, email: p.email, status: p.status },
          roles: p.roles.map(roleLabel),
          type: d.type,
          type_label: TYPE_LABELS[d.type] ?? d.type,
          effective_date: d.effective_date,
          note: d.note,
          deactivated_by: d.deactivated_by,
          deactivated_at: d.deactivated_at,
          reassigned_to: d.reassigned_to ? { id: d.reassigned_to, name: staffById(d.reassigned_to)?.name ?? "—" } : null,
          ownership: null,
          reactivation: d.reactivation,
          reactivated_at: d.reactivated_at,
        };
      });
    return ok(paginate(rows, query));
  }],

  ["get", "tenant/users", ({ query }) => {
    const rows = people
      .filter((p) => (query.status === "locked" ? isLocked(p) : !query.status || p.status === query.status))
      .filter((p) => !query.role || p.roles.includes(query.role))
      .filter((p) => matches(query.search, p.name, p.email, p.phone))
      .map(presentUser);
    return ok(paginate(rows, query, 100));
  }],

  ["get", "tenant/users/:id/ownership", ({ params }) => {
    const p = personById(params.id);
    if (!p) return notFound("That user is not in the sample data.");
    const report = ownershipOf(p);
    return ok({ ...report, total: report.total });
  }],

  ["post", "tenant/users/:id/ownership/reassign", ({ params, body }) => {
    const p = personById(params.id);
    if (!p) return notFound("That user is not in the sample data.");
    if (!body.to_staff_id) return invalid({ to_staff_id: ["Choose who takes over the records."] });
    if (!body.reason) return invalid({ reason: ["Give a reason for the reassignment."] });
    const report = ownershipOf(p);
    return ok({
      moved: report.items.filter((i) => i.reassignable).map(({ key, label, count }) => ({ key, label, count })),
      reported: report.items.filter((i) => !i.reassignable && i.count),
    }, `Records moved to ${staffById(body.to_staff_id)?.name ?? "the new owner"}.`);
  }],

  ["get", "tenant/users/:id", ({ params }) => {
    const p = personById(params.id);
    return p ? ok(presentUser(p)) : notFound("That user is not in the sample data.");
  }],

  ["post", "tenant/users", ({ body }) => {
    if (!body.name || !body.email) return invalid({ ...(body.name ? {} : { name: ["The name field is required."] }), ...(body.email ? {} : { email: ["The email field is required."] }) });
    if (people.some((p) => p.email.toLowerCase() === String(body.email).toLowerCase())) return invalid({ email: ["Somebody in this company already signs in with that email address."] });
    const p = {
      id: nextId(people), name: body.name, email: String(body.email).toLowerCase(), phone: body.phone ?? null, staff_id: body.staff_id ?? null,
      roles: (body.role_ids ?? []).map((id) => roles.find((r) => String(r.id) === String(id))?.name).filter(Boolean),
      status: "pending", mfa_enabled: false, last_login_at: null, failed_login_attempts: 0, locked_until: null, note: null, created_at: now(), activated: false,
    };
    people.unshift(p);
    return created(presentUser(p), `${p.name} has been invited. The invitation was emailed to ${p.email}.`);
  }],

  ["post", "tenant/users/:id/invite", ({ params }) => {
    const p = personById(params.id);
    if (!p) return notFound();
    if (p.activated) return conflict(`${p.name} has already activated their account.`);
    return ok(null, `A new invitation was emailed to ${p.email}`);
  }],

  ["post", "tenant/users/:id/deactivate", ({ params, body }) => {
    const p = personById(params.id);
    if (!p) return notFound();
    if (p.id === ME) return conflict("You cannot deactivate yourself. Ask another administrator.");
    if (!TYPE_LABELS[body.type]) return invalid({ type: ["Choose why access is ending."] });
    if (!String(body.note ?? "").trim()) return invalid({ note: ["Add a note: it goes into the inactive-user register."] });
    if (["deactivated", "suspended"].includes(p.status)) return conflict(`${p.name} is already without access.`);
    p.status = body.type === "suspension" ? "suspended" : "deactivated";
    deactivations.unshift({
      id: nextId(deactivations), user_id: p.id, type: body.type, effective_date: body.effective_date || today(), note: body.note,
      deactivated_by: { id: ME, name: "Jane Wanjiku" }, deactivated_at: now(), reassigned_to: body.reassign_to_staff_id ?? null, reactivation: null, reactivated_at: null,
    });
    return ok({ ...presentUser(p), ownership: ownershipOf(p) }, `${p.name} can no longer sign in. Their open sessions have ended.`);
  }],

  ["post", "tenant/users/:id/suspend", ({ params, body }) => {
    const p = personById(params.id);
    if (!p) return notFound();
    p.status = "suspended";
    deactivations.unshift({
      id: nextId(deactivations), user_id: p.id, type: "suspension", effective_date: today(), note: body.note ?? "Suspended",
      deactivated_by: { id: ME, name: "Jane Wanjiku" }, deactivated_at: now(), reassigned_to: null, reactivation: null, reactivated_at: null,
    });
    return ok(presentUser(p), `${p.name} can no longer sign in`);
  }],

  ["post", "tenant/users/:id/reactivate", ({ params, body }) => {
    const p = personById(params.id);
    if (!p) return notFound();
    if (!String(body.reason ?? "").trim()) return invalid({ reason: ["Give a reason; it goes to the approver."] });
    const d = openDeactivation(p.id);
    if (d?.reactivation?.status === "pending") return conflict(`${d.reactivation.reference} is already waiting on a decision for ${p.name}.`);
    const approval = { status: "pending", reference: `APR-2026-${String(200 + p.id).padStart(4, "0")}`, requested_by: "Jane Wanjiku", requested_at: now() };
    if (d) d.reactivation = approval;
    return ok({ user: presentUser(p), approval: { reference: approval.reference, status: "pending" } }, `Reactivating ${p.name} has gone for approval (${approval.reference}).`);
  }],

  ["post", "tenant/users/:id/unlock", ({ params }) => {
    const p = personById(params.id);
    if (!p) return notFound();
    p.locked_until = null;
    p.failed_login_attempts = 0;
    return ok(presentUser(p), `${p.name} can sign in again.`);
  }],

  ["post", "tenant/users/:id/mfa/reset", ({ params }) => {
    const p = personById(params.id);
    if (!p) return notFound();
    p.mfa_enabled = false;
    return ok(presentUser(p), `${p.name}'s two-step sign-in was reset. They enrol again at their next sign-in.`);
  }],

  /* ---- Data scopes (P0.4) ---- */
  ["get", "tenant/access/data-scopes/test", ({ query }) => {
    const p = personById(query.user_id);
    if (!p) return notFound("Choose a user.");
    const module = MODULES.find((m) => m.key === query.module) ?? MODULES[0];
    const byRole = p.roles.map((r) => ({ role: r, label: roleLabel(r), scope: r === "admin" ? "all" : matrix[r]?.[module.key] ?? "none" }));
    const temporary = grants
      .filter((g) => g.user_id === p.id && g.status === "active" && g.type === "scope" && g.module === module.key)
      .map((g) => ({ id: g.id, reference: g.reference, scope: g.scope, ends_at: g.ends_at }));
    const effective = widest([...byRole.map((r) => r.scope), ...temporary.map((t) => t.scope)]);
    const s = staffSummary(staffById(p.staff_id));
    const explanation = {
      none: `${module.label} is hidden from ${p.name}.`,
      own: `${p.name} sees only ${module.label.toLowerCase()} they own or are assigned to.`,
      team: s?.team ? `${p.name} sees ${module.label.toLowerCase()} owned by the ${s.team.name} team and by anyone who reports to them.` : `${p.name} is in no team, so team scope comes down to their own records and their reports'.`,
      department: s?.department ? `${p.name} sees ${module.label.toLowerCase()} owned by anyone in ${s.department.name}.` : `${p.name} is in no department, so department scope comes down to their team's records.`,
      all: `${p.name} sees every ${module.label.toLowerCase()} record in the company.`,
    }[effective];
    return ok({ user: { id: p.id, name: p.name }, module, by_role: byRole, temporary, effective, explanation });
  }],

  ["get", "tenant/access/data-scopes", () =>
    ok({
      scopes: SCOPES,
      modules: MODULES,
      roles: crmRoles.map((r) => ({ id: r.id, name: r.name, label: r.label, holders: people.filter((p) => p.status === "active" && p.roles.includes(r.name)).length, locked: r.name === "admin" })),
      matrix,
    })],

  ["put", "tenant/access/data-scopes", ({ body }) => {
    if (!matrix[body.role]) return notFound("No such role.");
    if (body.role === "admin") return conflict("The administrator always sees everything; their scope cannot be narrowed.");
    if (!MODULES.some((m) => m.key === body.module)) return invalid({ module: ["Choose a module."] });
    if (!RANK.includes(body.scope)) return invalid({ scope: ["Choose a scope."] });
    if (!String(body.reason ?? "").trim()) return invalid({ reason: ["Give a reason for the change; it goes into the change history."] });
    matrix[body.role][body.module] = body.scope;
    return ok({ role: body.role, module: body.module, scope: body.scope }, `${roleLabel(body.role)} now sees ${scopeLabel(body.scope).toLowerCase()} in ${moduleLabel(body.module)}.`);
  }],

  /* ---- Segregation of duties (P0.6) ---- */
  ["get", "tenant/access/sod-rules", () => ok({ rules, processes: PROCESSES, types: TYPES, maker_checker: makerChecker })],

  ["post", "tenant/access/sod-rules", ({ body }) => {
    if (!["maker_checker", "role_conflict", "admin_separation"].includes(body.type)) return invalid({ type: ["Choose the kind of rule."] });
    if (!body.label) return invalid({ label: ["Name the rule."] });
    const rule = { id: nextId(rules), key: `custom_${nextId(rules)}`, type: body.type, label: body.label, description: body.description ?? null, processes: body.processes ?? [], roles: body.roles ?? [], action: body.action ?? (body.type === "role_conflict" ? "block" : null), allowed_user_ids: [], is_active: true, is_locked: false };
    rules.push(rule);
    return created(rule, "Rule added");
  }],

  ["put", "tenant/access/sod-rules/:id", ({ params, body }) => {
    const rule = rules.find((r) => String(r.id) === params.id);
    if (!rule) return notFound();
    if (!String(body.reason ?? "").trim()) return invalid({ reason: ["Give a reason for the change; it goes into the change history."] });
    if (rule.is_locked && body.is_active === false) return conflict(`"${rule.label}" is part of the control baseline and cannot be switched off.`);
    for (const k of ["is_active", "label", "description", "processes", "roles", "action", "allowed_user_ids"]) if (k in body) rule[k] = body[k];
    return ok(rule, "Rule saved");
  }],

  ["delete", "tenant/access/sod-rules/:id", ({ params }) => {
    const i = rules.findIndex((r) => String(r.id) === params.id);
    if (i < 0) return notFound();
    if (rules[i].is_locked) return conflict(`"${rules[i].label}" is part of the control baseline and cannot be removed.`);
    rules.splice(i, 1);
    return ok(null, "Rule removed");
  }],

  /* ---- Temporary access (P0.7) ---- */
  ["get", "tenant/access/temporary-summary", () => ok(Object.fromEntries(Object.keys(STATUS_LABELS).map((k) => [k, grants.filter((g) => g.status === k).length])))],

  ["get", "tenant/access/temporary-options", () =>
    ok({
      roles: crmRoles.map((r) => ({ id: r.id, name: r.name, label: r.label })),
      permissions: PERMISSION_CHOICES,
      modules: MODULES,
      scopes: SCOPES.filter((s) => s.key !== "none"),
      max_days: policy.temporary_access.max_days,
    })],

  ["get", "tenant/access/temporary", ({ query }) => {
    const rows = grants
      .filter((g) => !query.status || g.status === query.status)
      .filter((g) => !query.user_id || String(g.user_id) === String(query.user_id))
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
      .map(presentGrant);
    return ok(paginate(rows, query, 50));
  }],

  ["post", "tenant/access/temporary", ({ body }) => {
    const errors = {};
    if (!body.user_id) errors.user_id = ["Choose who the access is for."];
    if (!["role", "permission", "scope"].includes(body.type)) errors.type = ["Choose the kind of access."];
    if (body.type === "role" && !body.role_id) errors.role_id = ["Choose the role."];
    if (body.type === "permission" && !(body.permissions ?? []).length) errors.permissions = ["Choose at least one permission."];
    if (body.type === "scope" && (!body.module || !body.scope)) errors.module = ["Choose the module and the scope."];
    if (!body.starts_at) errors.starts_at = ["Give a start date."];
    if (!body.ends_at) errors.ends_at = ["Give an expiry date."];
    if (!String(body.reason ?? "").trim()) errors.reason = ["Give a reason; the approver sees it."];
    if (Object.keys(errors).length) return invalid(errors);
    const days = Math.round((new Date(body.ends_at) - new Date(body.starts_at)) / 864e5) + 1;
    if (days < 1) return invalid({ ends_at: ["The expiry must be on or after the start."] });
    if (days > policy.temporary_access.max_days) return invalid({ ends_at: [`Temporary access may last at most ${policy.temporary_access.max_days} days.`] });
    const g = {
      id: nextId(grants), reference: `TAG-2026-${String(nextId(grants)).padStart(4, "0")}`, user_id: Number(body.user_id) || body.user_id, type: body.type,
      role: body.type === "role" ? roles.find((r) => String(r.id) === String(body.role_id)) : null,
      permissions: body.type === "permission" ? body.permissions : [], module: body.type === "scope" ? body.module : null, scope: body.type === "scope" ? body.scope : null,
      starts_at: body.starts_at, ends_at: body.ends_at, reason: body.reason, status: "pending", requested_by: { id: ME, name: "Jane Wanjiku" }, approver_name: null, created_at: now(),
    };
    grants.unshift(g);
    return created(presentGrant(g), "Sent for approval. Somebody other than you, and other than the person it is for, decides.");
  }],

  ["post", "tenant/access/temporary/:id/approve", ({ params }) => {
    const g = findGrant(params.id);
    if (!g) return notFound();
    if (g.status !== "pending") return conflict("This request has already been decided.");
    if (g.user_id === ME || g.requested_by?.id === ME) return fail("You raised this request or it is for you, so somebody else must approve it.", { code: "SOD_SELF_APPROVAL" }, 403);
    g.approver_name = "Jane Wanjiku";
    g.status = g.starts_at <= today() ? "active" : "scheduled";
    return ok(presentGrant(g), g.status === "active" ? "Approved — the access is in force now." : "Approved — it starts on its start date.");
  }],

  ["post", "tenant/access/temporary/:id/reject", ({ params, body }) => {
    const g = findGrant(params.id);
    if (!g) return notFound();
    if (!String(body.comments ?? "").trim()) return invalid({ comments: ["Say why, so the person who asked knows."] });
    g.status = "rejected";
    g.approver_name = "Jane Wanjiku";
    g.revoke_reason = body.comments;
    return ok(presentGrant(g), "Rejected");
  }],

  ["post", "tenant/access/temporary/:id/revoke", ({ params, body }) => {
    const g = findGrant(params.id);
    if (!g) return notFound();
    if (!["active", "scheduled"].includes(g.status)) return conflict("Only access that is in force or scheduled can be revoked.");
    if (!String(body.reason ?? "").trim()) return invalid({ reason: ["Give a reason for revoking it."] });
    g.status = "revoked";
    g.revoke_reason = body.reason;
    g.revoked_at = now();
    g.revoked_by_name = "Jane Wanjiku";
    return ok(presentGrant(g), "Access revoked.");
  }],

  ["post", "tenant/access/temporary/:id/cancel", ({ params }) => {
    const g = findGrant(params.id);
    if (!g) return notFound();
    if (g.status !== "pending") return conflict("Only a request still waiting on a decision can be called off.");
    g.status = "cancelled";
    return ok(presentGrant(g), "Request cancelled");
  }],

  /* ---- Security policy (P0.10) ---- */
  ["get", "tenant/security/policy", () =>
    ok({
      policy,
      meta: { mfa_modes: MFA_MODES, roles: crmRoles.map((r) => ({ name: r.name, label: r.label })), days: DAYS, your_ip: "41.90.64.12" },
    })],

  ["put", "tenant/security/policy", ({ body }) => {
    if (!String(body.reason ?? "").trim()) return invalid({ reason: ["Give a reason for the change; it goes into the change history."] });
    for (const section of ["password", "lockout", "session", "mfa", "ip", "hours", "temporary_access"]) {
      if (body[section] && typeof body[section] === "object") Object.assign(policy[section], body[section]);
    }
    if (policy.password.min_length < 8) return invalid({ "password.min_length": ["A password must be at least 8 characters."] });
    policy.updated_at = now();
    policy.updated_by = "Jane Wanjiku";
    return ok(policy, "Security policy saved. It applies at each person's next sign-in.");
  }],

  ["get", "tenant/auth/password-policy", () => {
    const p = policy.password;
    const rules = [`At least ${p.min_length} characters`];
    if (p.require_upper) rules.push("An upper-case letter");
    if (p.require_lower) rules.push("A lower-case letter");
    if (p.require_number) rules.push("A number");
    if (p.require_symbol) rules.push("A symbol");
    if (p.disallow_name) rules.push("Not your name or email");
    if (p.history) rules.push(`Not one of your last ${p.history} passwords`);
    return ok({ ...p, rules });
  }],

  /* ---- Two-step sign-in ---- */
  ["get", "tenant/auth/mfa", () => ok({ ...mine, required: false, mode: policy.mfa.mode })],
  ["post", "tenant/auth/mfa/enrol", () => {
    if (mine.enabled) return conflict("Two-step sign-in is already on. Turn it off first to enrol a new device.");
    mine.pending = true;
    return ok(enrolment());
  }],
  ["post", "tenant/auth/mfa/confirm", ({ body }) => {
    if (!mine.pending) return conflict("Start enrolling first.");
    if (!/^\d{6}$/.test(String(body.code ?? "").trim())) return invalid({ code: ["Enter the 6-digit code your authenticator app shows."] });
    Object.assign(mine, { enabled: true, enabled_at: now(), pending: false, recovery_codes_left: 8 });
    return ok({ enabled: true, recovery_codes: recoveryCodes() }, "Two-step sign-in is on.");
  }],
  ["post", "tenant/auth/mfa/disable", ({ body }) => {
    if (!body.password) return invalid({ password: ["Enter your password to turn it off."] });
    Object.assign(mine, { enabled: false, enabled_at: null, pending: false, recovery_codes_left: 0 });
    return ok({ enabled: false }, "Two-step sign-in is off.");
  }],
  ["post", "tenant/auth/mfa/recovery-codes", ({ body }) => {
    if (!body.password) return invalid({ password: ["Enter your password."] });
    mine.recovery_codes_left = 8;
    return ok({ recovery_codes: recoveryCodes() }, "New recovery codes. The old ones no longer work.");
  }],
  ["put", "tenant/auth/password", ({ body }) => {
    if (!body.current_password) return invalid({ current_password: ["Enter your current password."] });
    if (!body.password || body.password !== body.password_confirmation) return invalid({ password: ["The passwords do not match."] });
    return ok(null, "Password changed.");
  }],

  // The second step of a sign-in. Prototype mode signs itself in, so these only answer.
  ["post", "tenant/auth/mfa/challenge/enrol", () => ok(enrolment())],
  ["post", "tenant/auth/mfa/challenge", ({ body }) =>
    codeOk(body.code) ? ok(tenantSession(), "Login successful") : invalid({ code: ["That code is not right. Check the time on your phone and try again."] })],
  ["post", "tenant/auth/password/renew", ({ body }) => {
    if (!body.password || body.password !== body.password_confirmation) return invalid({ password: ["The passwords do not match."] });
    return ok(tenantSession(), "Password changed. You are signed in.");
  }],
];
